import type { PreviewVetterPriceRequestQuery } from '@codezo/graphql';
import { Radio } from '../../ui/radio';

export type VetterRecipient =
	PreviewVetterPriceRequestQuery['previewVetterPriceRequest']['recipients'][number];

/** Picker value: no single recipient — every account holder is emailed. */
export const EVERYONE = '__everyone__';

/**
 * Who at the supplier the request is sent to: everyone with an account (only
 * offered when there is someone), or one person. The backend takes no subset —
 * nobody, meaning every account holder, or exactly one person. Picking one
 * emails only them; the other account holders still see it in their portal.
 *
 * People are the supplier's own, managed on the supplier's page. How each is
 * reached (portal or secure link) is the backend's `deliveryLabel`.
 */
export function VetterRecipientPicker({
	people,
	everyoneLabel,
	value,
	onChange,
	disabled = false,
}: {
	people: VetterRecipient[];
	/** When set, the "everyone with an account" option and its sub-line. */
	everyoneLabel?: { title: string; sub: string } | null;
	/** EVERYONE or a vetterUserId. */
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
}) {
	const options = [
		...(everyoneLabel ? [{ key: EVERYONE, ...everyoneLabel }] : []),
		...people.map((p) => ({
			key: p.vetterUserId,
			title: p.name,
			sub: [p.email, p.deliveryLabel].filter(Boolean).join(' · '),
		})),
	];

	return (
		<div className="flex flex-col divide-y divide-line-1 rounded-[var(--r-3)] border border-line-1">
			{options.map((o) => (
				<button
					key={o.key}
					type="button"
					onClick={() => onChange(o.key)}
					disabled={disabled}
					className="flex w-full cursor-pointer items-start gap-2.5 bg-transparent px-3 py-2.5 text-left transition-colors hover:bg-surface-hover"
				>
					<Radio checked={value === o.key} className="mt-0.5" />
					<div className="min-w-0 flex-1">
						<div className="truncate text-sm text-text-1">{o.title}</div>
						<div className="text-xs text-text-4">{o.sub}</div>
					</div>
				</button>
			))}
		</div>
	);
}
